/**
 * Content Loaders für Page-Based JSON Structure
 * Lädt JSON via fetch() aus public/content/
 */

import { Language } from '@/core/config/language-v2';

export type PageContent = Record<string, any>;

// Cache for loaded content
const contentCache = new Map<string, PageContent>();

/**
 * Map route to page name
 */
function getPageFromRoute(route: string): string {
  const path = route.toLowerCase();
  
  if (path === '/' || path === '') return 'landing';
  if (path.startsWith('/test')) return 'test';
  if (path.startsWith('/results')) return 'results';
  if (path.startsWith('/email')) return 'email';
  if (path.startsWith('/about')) return 'about';
  if (path.startsWith('/contact')) return 'contact';
  if (path.startsWith('/faq')) return 'faq';
  if (path.startsWith('/privacy')) return 'privacy';
  if (path.startsWith('/terms')) return 'terms';
  
  // Default to landing for unknown routes
  return 'landing';
}

/**
 * Deep merge two objects
 */
function deepMerge(target: any, source: any): any {
  if (!source) return target;
  if (!target) return source;
  
  const output = { ...target };
  
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      output[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  }
  
  return output;
}

/**
 * Fetch JSON file from public/content/
 */
async function fetchJSON(language: Language, filename: string): Promise<PageContent> {
  const cacheKey = `${language}:${filename}`;
  
  // Check cache first
  if (contentCache.has(cacheKey)) {
    return contentCache.get(cacheKey)!;
  }
  
  try {
    const response = await fetch(`/content/burnout-test-v2/${language}/${filename}.json`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    
    // Cache the result
    contentCache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error(`Failed to load ${filename} for ${language}:`, error);
    return {};
  }
}

/**
 * Load content for a specific page and language (ASYNC)
 * Merges global content with page-specific content
 */
export async function loadPageContent(
  language: Language,
  route: string
): Promise<PageContent> {
  const pageName = getPageFromRoute(route);
  
  try {
    // Load global and page-specific content in parallel
    const [globalContent, pageContent] = await Promise.all([
      fetchJSON(language, '_global'),
      fetchJSON(language, pageName),
    ]);
    
    // Merge global content with page-specific content
    return deepMerge(globalContent, pageContent);
  } catch (error) {
    console.error(`Failed to load page content for ${language}:${route}:`, error);
    return {};
  }
}

/**
 * Load only global content (for shared components like nav, footer)
 */
export async function loadGlobalContent(language: Language): Promise<PageContent> {
  return fetchJSON(language, '_global');
}

/**
 * Preload all content for a language (for optimization)
 */
export async function preloadLanguage(language: Language): Promise<void> {
  const pages = ['_global', 'landing', 'test', 'results', 'email', 'about', 'contact', 'faq', 'privacy', 'terms'];
  
  await Promise.all(
    pages.map(page => fetchJSON(language, page))
  );
}
